import { FormField } from '@components/admission-modal/admission/admission.type';
import { BookingFormField } from '@domain/booking-form-field';
import { FormFieldPreset } from '@domain/form-field-presets';
import { TicketHolderFormField } from '@domain/ticket-holder-form-field';

import { FIELD_FIRST_NAME, FIELD_LAST_NAME, FIELD_TAX_ID_PT } from './validate-import-csv';

interface ForamtTicketHolderProps {
  formFields: FormFieldPreset[];
  isFormFieldDisabled?: boolean;
  offerTicketHolderFormFields?: TicketHolderFormField[];
}

export const mergeFormFields = (
  initialticketHolderFields: FormField[],
  offerTicketHolderFormFields?: TicketHolderFormField[]
) =>
  initialticketHolderFields.map((field) => {
    const isFieldInOffer = offerTicketHolderFormFields?.find((offerField) => offerField.name === field.name);
    if (!isFieldInOffer) return field;

    // taxIdPt is always persisted as optional (isRequired:false), but legacy's binary checkbox reads
    // isRequired, so a collected NIF would load unchecked. Force-check it while active so it round-trips.
    const isActiveTaxIdPt = field.slug === FIELD_TAX_ID_PT && isFieldInOffer.isActive !== false;

    return {
      ...field,
      isRequired: isActiveTaxIdPt ? true : isFieldInOffer.isRequired,
    };
  });

export const filterNewOfferFormFields = ({
  offerFormFields,
  initialFormFields,
  includeMinMax,
  isFormFieldDisabled,
}: {
  offerFormFields?: TicketHolderFormField[] | BookingFormField[];
  initialFormFields: FormField[];
  includeMinMax: boolean;
  isFormFieldDisabled: boolean;
}) => {
  return (
    offerFormFields
      ?.filter(
        (field) =>
          !initialFormFields.find((initialField) => initialField.name === field.name) &&
          field.slug !== FIELD_FIRST_NAME &&
          field.slug !== FIELD_LAST_NAME
      )
      .map((field) => ({
        dataType: field.dataType,
        disabled: isFormFieldDisabled,
        isRequired: field.isRequired,
        name: field.name,
        slug: field.slug ?? null,
        ...(includeMinMax && {
          min: (field as BookingFormField).min === 0 ? 0 : (field as BookingFormField).min,
          max: (field as BookingFormField).max,
        }),
      })) ?? []
  );
};

export const ticketHolderPresetToFormField = ({
  formFields,
  isFormFieldDisabled = false,
}: Omit<ForamtTicketHolderProps, 'offerTicketHolderFormFields'>) => {
  return formFields.map(({ name, slug, dataType, isAssignedByDefault }) => {
    return {
      name,
      slug,
      dataType,
      isRequired: isAssignedByDefault,
      disabled: isFormFieldDisabled,
    };
  });
};

export const formatTicketHolderFromBackend = ({
  formFields,
  isFormFieldDisabled = false,
  offerTicketHolderFormFields,
}: ForamtTicketHolderProps) => {
  const initialticketHolderFields = ticketHolderPresetToFormField({
    formFields,
    isFormFieldDisabled,
  });

  const ticketHolderFormFields = mergeFormFields(initialticketHolderFields, offerTicketHolderFormFields);

  const newTicketHolderFormFields = filterNewOfferFormFields({
    offerFormFields: offerTicketHolderFormFields,
    initialFormFields: ticketHolderFormFields,
    includeMinMax: false,
    isFormFieldDisabled,
  });

  return [...ticketHolderFormFields, ...newTicketHolderFormFields];
};
